import { useEffect, useState } from "react";
import { Gift } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, Card, SectionTitle, Field, SubmitButton, EmptyState, LoadingState, ErrorState, StatusBadge } from "@/components/common";
import { PlanGate } from "@/components/PlanGate";
import { useGarage } from "@/context/GarageContext";
import { useFetch } from "@/lib/useFetch";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { t } from "@/i18n";

function SettingsCard({ initial, onSaved, readOnly }) {
  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState(false);
  useEffect(() => setF(initial), [initial]);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.put("/referrals/settings", { ...f, reward_value: Number(f.reward_value) || 0 }); toast.success(t("common.saved")); onSaved(); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Card testId="referral-settings-card">
      <SectionTitle hint={t("ref.settingsHint")}>{t("ref.settings")}</SectionTitle>
      <form onSubmit={save} className="space-y-4">
        <label className="flex items-center gap-2 text-sm"><Switch checked={f.enabled} disabled={readOnly} onCheckedChange={(v) => setF({ ...f, enabled: v })} data-testid="referral-enabled-switch" /> {t("ref.enabled")}</label>
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label={t("ref.rewardType")}>
            <Select value={f.reward_type} onValueChange={(v) => setF({ ...f, reward_type: v })} disabled={readOnly}>
              <SelectTrigger data-testid="referral-reward-type"><SelectValue /></SelectTrigger>
              <SelectContent>{["credit", "percent", "text"].map((k) => <SelectItem key={k} value={k}>{t(`ref.types.${k}`)}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          {f.reward_type !== "text"
            ? <Field label={f.reward_type === "credit" ? t("ref.amount") : t("ref.percent")}><Input type="number" min={0} step="0.01" value={f.reward_value} disabled={readOnly} onChange={(e) => setF({ ...f, reward_value: e.target.value })} data-testid="referral-reward-value" /></Field>
            : <Field label={t("ref.text")} className="sm:col-span-2"><Input value={f.reward_text} disabled={readOnly} onChange={(e) => setF({ ...f, reward_text: e.target.value })} data-testid="referral-reward-text" /></Field>}
        </div>
        {!readOnly && <SubmitButton busy={busy} data-testid="referral-settings-save">{t("common.save")}</SubmitButton>}
      </form>
    </Card>
  );
}

function Inner() {
  const { garage, readOnly } = useGarage();
  const { data, loading, error, reload } = useFetch("/referrals");
  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  const use = async (id) => { try { await api.post(`/referrals/${id}/mark-used`); toast.success(t("ref.markedUsed")); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="space-y-6">
      <PageHeader title={t("ref.title")} subtitle={t("ref.subtitle")} />
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {["pending", "earned", "used", "rejected"].map((k) => <Card key={k} testId={`ref-summary-${k}`}><p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t(`status.${k}`)}</p><p className="mt-1 text-3xl font-extrabold">{data.summary[k]}</p></Card>)}
      </div>
      <SettingsCard initial={data.settings} onSaved={reload} readOnly={readOnly} />
      <Card testId="referrals-list">
        <SectionTitle hint={t("ref.rule")}>{t("ref.list")}</SectionTitle>
        {data.items.length === 0 ? <EmptyState icon={Gift} title={t("ref.empty")} text={t("ref.emptyText")} /> : (
          <div className="divide-y divide-slate-100">
            {data.items.map((r) => (
              <div key={r.id} className="flex flex-col gap-2 py-3 text-sm sm:flex-row sm:items-center sm:justify-between" data-testid={`referral-row-${r.id}`}>
                <div><strong>{r.referrer.name}</strong> → {r.referred.name}<p className="text-xs text-slate-500">{fmtDateTime(r.created_at, garage.timezone)}{r.reject_reason && ` · ${t(`ref.reasons.${r.reject_reason}`)}`}</p></div>
                <div className="flex items-center gap-2">
                  {r.reward && <span className="text-xs text-slate-600">{r.reward.label}</span>}
                  <StatusBadge status={r.status} testId={`referral-status-${r.id}`} />
                  {r.status === "earned" && !readOnly && <Button size="sm" variant="outline" onClick={() => use(r.id)} data-testid={`referral-mark-used-${r.id}`}>{t("ref.markUsed")}</Button>}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

export default function Referrals() {
  return <PlanGate feature="referral" managers><Inner /></PlanGate>;
}
